/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect } from 'react';
import _ from 'lodash';
import { useSecurity } from '../../Security/security';
import { StoreContext } from '../../Store';
import ControlledGoodsModal from '../../ControlledGoodsModal';
import CustomsValueModal from './CustomsValueModal';
import ListOfDocumentTypes from './ListOfDocumentTypes';
import SubmitButtons from './SubmitButtons';
import OrderFormMultiRulesModal from './OrderFormMultiRulesModal';
import { getFormDataToOrderForms, isChedDoc, convertToGbp } from '../QAChecklistTab/utils';
import { useDropdownOptions } from '../queryHooks/useDropdownOptions';
import { useExchangeRates } from '../queryHooks/useExchangeRates';
import { isLoadStatusSubmitted } from '../../../utils/unifiedLoads';
import { QAStoreContext } from '../QAChecklistTab/QAStore';
import { useMutation } from '@tanstack/react-query';
import { getOrderForms, postOrderForms } from '../../../api/v2/load';

const OrderForm = ({
  visible,
  firstPage,
  submit,
  showQATab,
  nextPage,
  createLoadToPost,
  selectedForms,
  setSelectedForms,
  readOnly = false,
  currentId,
  setCurrentTab,
}) => {
  const store = useContext(StoreContext);
  const { validateRole } = useSecurity();
  const [showControlledGoodsModal, setShowControlledGoodsModal] = useState(false);
  const [showCustomsValueModal, setShowCustomsValueModal] = useState(false);
  const [noneSelected, setNoneSelected] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(true);
  const [openMultiOrderFormModal, setOpenMultiOrderFormModal] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [nonPosFA, setNonPosFA] = useState(false);

  const { unifiedLoadData } = useContext(QAStoreContext);

  const referenceNoDisplay = store.portalRef[0] ? ` - ${store.portalRef[0]}` : '';

  const getOrderFormsMutation = useMutation({
    mutationFn: (loadId) => getOrderForms(loadId),
  });

  const postOrderFormsMutation = useMutation({
    mutationFn: (data) => postOrderForms(data),
  });

  const { staticDropdowns } = useDropdownOptions();
  const { routes, currencies } = staticDropdowns.data || {};

  const { fetchExchangeRate } = useExchangeRates(currencies);

  const isLoadSubmitted = isLoadStatusSubmitted(store.loadStatus[0]);

  const nonPositiveFreightAllocation = async () => {
    const freightCurrency = await fetchExchangeRate(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.currency || 0),
      store.date[0]
    );
    const insuranceCurrency = await fetchExchangeRate(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.currency || 0),
      store.date[0]
    );
    const otherCurrency = await fetchExchangeRate(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.currency || 0),
      store.date[0]
    );
    const invoiceCurrency = await fetchExchangeRate(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.currency || 0),
      store.date[0]
    );
    const freightCharges = convertToGbp(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'freightCharges')?.amount || 0),
      freightCurrency
    );
    const insuranceCharges = convertToGbp(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'insuranceCharges')?.amount || 0),
      insuranceCurrency
    );
    const otherCharges = convertToGbp(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'otherCharges')?.amount || 0),
      otherCurrency
    );
    const invoiceDiscount = convertToGbp(
      Number(store.adjustments[0]?.find((adj) => adj.name === 'invoiceDiscount')?.amount || 0),
      invoiceCurrency
    );
    const totalItemValue = convertToGbp(Number(store.totalItemValue[0]), store.exchangeRate[0]);

    if (totalItemValue === 0) {
      setNonPosFA(true);
      return;
    }
    const hasNonPositiveFreightAllocation = store.validatedRowsReadyToSubmit[0]?.some(
      (item) =>
        convertToGbp(item.itemValue, store.exchangeRate[0]) +
          (convertToGbp(item.itemValue, store.exchangeRate[0]) / totalItemValue) * freightCharges +
          (convertToGbp(item.itemValue, store.exchangeRate[0]) / totalItemValue) * insuranceCharges +
          (convertToGbp(item.itemValue, store.exchangeRate[0]) / totalItemValue) * otherCharges -
          (convertToGbp(item.itemValue, store.exchangeRate[0]) / totalItemValue) * invoiceDiscount <=
        0
    );
    setNonPosFA(hasNonPositiveFreightAllocation);
  };

  const handleLoadSubmitClick = () => {
    if (store.ffd[0] || store.GBimport[0]) {
      if (nonPosFA) {
        setShowCustomsValueModal(true);
      } else if (store.GBimport[0] && store.controlledGoods[0] === 'N' && !noneSelected) {
        setShowControlledGoodsModal(true);
      } else {
        setDisableSubmit(true);
        submit(window.event);
        setNoneSelected(false);
      }
    } else if (store.GBimport[0] && store.controlledGoods[0] === 'N' && !noneSelected) {
      setShowControlledGoodsModal(true);
    } else {
      setDisableSubmit(true);
      submit(window.event);
      setNoneSelected(false);
    }
  };

  const handleQASubmitClick = () => {
    if (isLoadSubmitted) {
      nextPage();
      return;
    }
    if (store.ffd[0] || store.GBimport[0]) {
      if (nonPosFA) {
        setShowCustomsValueModal(true);
      } else if (store.GBimport[0] && store.controlledGoods[0] === 'N' && !noneSelected) {
        setShowControlledGoodsModal(true);
      } else {
        setDisableSubmit(true);
        createLoadToPost();
        nextPage();
      }
    } else if (store.GBimport[0] && store.controlledGoods[0] === 'N' && !noneSelected) {
      setShowControlledGoodsModal(true);
    } else {
      setDisableSubmit(true);
      createLoadToPost();
      nextPage();
    }
  };

  useEffect(() => {
    const fetch = async () => {
      await nonPositiveFreightAllocation();
    };
    fetch();
  }, [visible]);

  const findById = (arr, id) => {
    const result = arr?.find((item) => item?.id?.toString() === id?.toString());
    if (result) return result;
    return {};
  };

  const fetchNewDocument = async () => {
    const roleIsValid = validateRole({ department: 'QA' });
    const postData = getFormDataToOrderForms(store, roleIsValid);

    const loadHeaders = unifiedLoadData[0]?.Headers?.[0];
    const isEPO = loadHeaders?.loadType === 'EPO';
    const results = await postOrderFormsMutation.mutateAsync(postData);

    const savedDocument = isEPO ? await getOrderFormsMutation.mutateAsync(loadHeaders?.id) : [];

    let forms = results?.[0]?.Docs;

    // For submitted loads, we need to check the forms returned from the DB
    // TODO: All this logic probably needs rewriting at some point, as it's very confusing
    // The backend should always return whether forms are selected - but it doesn't currently
    if (isLoadSubmitted) {
      forms = forms.map((doc) => {
        return {
          ...doc,
          checked: selectedForms.includes(doc.formname),
        };
      });
      store.docTypes[1](forms);
    }

    const shouldFillDirectly = results?.length === 1 || selectedForms || store.docTypes[0].length;

    if (shouldFillDirectly) {
      const checkRules =
        isEPO && !store.docTypes[0].length
          ? {
              isEPO,
              GBimport: store.GBimport[0] ?? null,
              CABIEIpaffs: loadHeaders?.CABIEIpaffs ?? null,
              healthcertUploaded: loadHeaders?.healthcertUploaded ?? null,
              transitExclude: loadHeaders?.transitExclude ?? null,
              ensExclude: loadHeaders?.ensExclude ?? null,
              existingDocs: JSON.parse(savedDocument?.[0]?.orderforms ?? '[]') ?? [],
            }
          : {};
      fillOrderForms(forms, checkRules);
    } else {
      setModalData(results);
      setOpenMultiOrderFormModal(true);
    }
  };

  const addDefaultQty = (docs) =>
    docs.map((document) => {
      const isChed = isChedDoc(document);
      let qty = document?.qty;

      if (isChed) {
        qty = store.qTracesChed?.[0] > 0 ? store.qTracesChed[0] : (document.qty ?? 1);
      }

      return { ...document, qty };
    });

  const getPreTickedChecked = (formname, preTickRules) => {
    const { GBimport, CABIEIpaffs, healthcertUploaded, transitExclude, ensExclude, existingDocs } = preTickRules;

    if (existingDocs.includes(formname)) return true;

    switch (formname) {
      case 'docTypegbimp':
        return GBimport === true;

      case 'docTypeexport':
        return GBimport === false;

      case 'docTypeens':
        return ensExclude === false;

      case 'docTypetraces':
        return (CABIEIpaffs === true && GBimport === true) || (healthcertUploaded === true && GBimport === false);

      case 'docTypetransit':
        return transitExclude === false;

      default:
        return false;
    }
  };

  const fillOrderForms = (results, preTickRules = {}) => {
    const { isEPO, ...selectionRules } = preTickRules ?? {};
    store.orderFormId[1](results?.[0]?.orderFormId);

    // EPO rules are only passed on the first fill (docTypes empty), so apply them regardless of docTypes length
    if (isEPO) {
      store.docTypes[1](
        addDefaultQty(results)?.map((document) => ({
          ...document,
          checked: getPreTickedChecked(document.formname, selectionRules),
        }))
      );
    } else if (store.docTypes[0]?.length) {
      store.docTypes[1]((prev) => {
        const tickedValues = prev.filter((v) => v.checked).map((v) => v.formname);

        const response = addDefaultQty(results)?.map((document) => ({
          ...document,
          checked: tickedValues.includes(document.formname),
        }));

        return response;
      });
    } else {
      store.docTypes[1](addDefaultQty(results));
    }
  };
  useEffect(() => {
    store.qTracesChed?.[1]?.(0);
  }, [currentId]);

  useEffect(() => {
    if (visible && store.clientID[0]) {
      fetchNewDocument();
    }
  }, [visible, currentId, store.clientID[0]]);

  useEffect(() => {}, [
    store.directionSelected[0],
    store.controlledGoods[0],
    store.officeOfDest[0],
    store.validatedRowsReadyToSubmit[0],
  ]);

  useEffect(() => {
    const isAnyChecked = store.docTypes[0].some((form) => form.checked);
    isAnyChecked ? setDisableSubmit(false) : setDisableSubmit(true);
  }, [store?.docTypes[0]]);

  /**
   * Updates a document in the `documents` variable.
   * @param {Number} idx index of document that is being updated
   * @param {Object} newDocument Values to replace old values with in document.
   */
  function setDocumentsByIndex(idx, newDocument, title) {
    const titleLower = (title || '').toLowerCase();
    const isChed = isChedDoc({ title: titleLower, formname: store.docTypes[0]?.[idx]?.formname || '' });
    if (titleLower === 'gb ffd entry') {
      //if the document title is gb ffd entry, we set this document to true or false, and we need to look for the record where title is 'gb import entry' and set it to false
      const gbImportEntryIndex = store.docTypes[0].findIndex((doc) => doc.title.toLowerCase() === 'gb import entry');
      store.docTypes[1]((prev) => {
        prev[idx] = { ...prev[idx], ...newDocument };
        if (gbImportEntryIndex !== -1) prev[gbImportEntryIndex] = { ...prev[gbImportEntryIndex], checked: false };
        return [...prev];
      });
    } else if (titleLower === 'gb import entry') {
      //if the document title is gb import entry, we set this index to true or false, and we need to look for the record where title is 'gb ffd entry' and set it to false
      const gbFFDEntry = store.docTypes[0].findIndex((doc) => doc.title.toLowerCase() === 'gb ffd entry');
      store.docTypes[1]((prev) => {
        prev[idx] = { ...prev[idx], ...newDocument };
        if (gbFFDEntry !== -1) prev[gbFFDEntry] = { ...prev[gbFFDEntry], checked: false };
        return [...prev];
      });
    } else {
      store.docTypes[1]((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...newDocument };
        if (isChed && newDocument.checked) {
          next[idx].qty = newDocument.qty ?? next[idx].qty ?? 1;
          store.qTracesChed?.[1]?.(next[idx].qty);
        }
        if (isChed && typeof newDocument.qty !== 'undefined') {
          next[idx].qty = newDocument.qty;
          store.qTracesChed?.[1]?.(newDocument.qty);
        }
        return next;
      });
    }
  }

  return (
    <div className="mb-4 semi-fluid-container container-lg">
      <OrderFormMultiRulesModal
        show={openMultiOrderFormModal}
        onHide={() => setOpenMultiOrderFormModal(false)}
        modalData={modalData}
        fillOrderForms={fillOrderForms}
      />

      <section className="floating-box-newload">
        <h1 className="floating-box-header-orderForm">
          <span>
            Order Form{' '}
            {!!store.clientID[0] && !validateRole({ isCustomer: true }) && (
              <span style={{ fontSize: '1.0rem', marginTop: '0.3rem' }}>
                ({store.clientIDname[0]}
                {referenceNoDisplay})
              </span>
            )}
          </span>
          <span style={{ marginTop: '10px', fontSize: '1.2rem', fontWeight: '500' }}>
            Route: {findById(routes, store.directionSelected[0]).data}
          </span>
        </h1>
        <ListOfDocumentTypes
          visible={visible}
          docTypes={store.docTypes[0]}
          readOnly={readOnly}
          setDocumentsByIndex={setDocumentsByIndex}
        />
        <SubmitButtons
          readOnly={readOnly}
          showQATab={showQATab}
          handleQASubmitClick={handleQASubmitClick}
          handleLoadSubmitClick={handleLoadSubmitClick}
          disableSubmit={disableSubmit}
        />
        <ControlledGoodsModal
          header="Controlled Goods"
          size="md"
          showButtons={true}
          animation={false}
          show={showControlledGoodsModal}
          onHide={() => setShowControlledGoodsModal(false)}
          subTitle={
            'As you have not selected "Controlled Goods" on the Load Header form, please confirm this by pressing NONE below; if this load does contain Controlled Goods, press CONTAINS to the return to the Load Header where you should select Yes from the dropdown.'
          }
          onConfirm={firstPage}
          onNone={() => setNoneSelected(true)}
        ></ControlledGoodsModal>
        <CustomsValueModal
          header="Invalid Customs Value"
          size="md"
          animation={false}
          showButtons={true}
          show={showCustomsValueModal}
          onHide={() => setShowCustomsValueModal(false)}
          subTitle={
            'The calculated Customs Value for the load is less than or equal to Zero. Please review the Other deductions in the Adjustments section.'
          }
          onConfirm={setCurrentTab}
        ></CustomsValueModal>
      </section>
    </div>
  );
};

export default OrderForm;
